import { useState, useMemo, useCallback } from 'react';
import { ExpertiseData } from '../types/pipeline.types';
import { PIPELINE_STAGES } from '../constants/stages.constants';
import { calculateUrgency } from '@/ui/components/molecules/ExpertiseCard/utils/tagRules';
import { ExpertiseStatus } from '@/ui/components/molecules/ExpertiseCard/ExpertiseCard.types';

export const usePipelineStages = (expertises: ExpertiseData[]) => {
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  
  // Calculate counts per stage
  const stageCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    
    // Initialize all stages with 0
    PIPELINE_STAGES.forEach(stage => {
      counts[stage.id] = 0;
    });
    
    // Count expertises per stage
    expertises.forEach(expertise => {
      if (expertise.stage && counts[expertise.stage] !== undefined) {
        counts[expertise.stage]++;
      }
    });
    
    return counts;
  }, [expertises]);
  
  // Calculate urgent counts per stage
  const urgentCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    
    // Initialize all stages with 0
    PIPELINE_STAGES.forEach(stage => {
      counts[stage.id] = 0;
    });
    
    // Count urgent expertises per stage
    expertises.forEach(expertise => {
      if (expertise.stage && counts[expertise.stage] !== undefined) {
        // Check if expertise is urgent using the same logic as ExpertiseCard
        const isUrgent = calculateUrgency({
          id: expertise.id,
          reference: expertise.reference,
          title: expertise.type || expertise.type_expertise || '',
          status: (expertise.status || 'en_cours') as ExpertiseStatus,
          stageId: expertise.stage,
          clientName: expertise.client_name,
          expertName: expertise.expert,
          createdAt: expertise.date || expertise.date_demande,
          dueDate: expertise.date_du_rv,
          tags: expertise.tags || [],
          priority: expertise.isUrgent ? 'urgent' : 'medium'
        });
        
        if (isUrgent) {
          counts[expertise.stage]++;
        }
      }
    });
    
    return counts;
  }, [expertises]);
  
  // Get expertises for selected stage
  const stageExpertises = useMemo(() => {
    if (!selectedStageId) return [];
    return expertises.filter(exp => exp.stage === selectedStageId);
  }, [selectedStageId, expertises]);
  
  // Handle stage selection
  const handleStageSelect = useCallback((stageId: number) => {
    // Toggle selection if clicking the same stage
    if (selectedStageId === stageId) {
      setSelectedStageId(null);
    } else if (stageCounts[stageId] > 0) {
      // Only select if stage has items
      setSelectedStageId(stageId);
    }
  }, [selectedStageId, stageCounts]);
  
  // Get selected stage details
  const selectedStage = useMemo(() => {
    if (!selectedStageId) return null;
    return PIPELINE_STAGES.find(s => s.id === selectedStageId);
  }, [selectedStageId]);
  
  // Calculate total expertises
  const totalExpertises = useMemo(() => {
    return Object.values(stageCounts).reduce((sum, count) => sum + count, 0);
  }, [stageCounts]);
  
  return {
    selectedStageId,
    selectedStage,
    stageCounts,
    urgentCounts,
    stageExpertises,
    totalExpertises,
    handleStageSelect,
    setSelectedStageId
  };
};